import cgmUpsell from 'assets/cgm_upsell.png'
import { VideoCalls } from 'hooks/analytics/events'
import dexcomG7 from 'images/cgm-g7.webp'
import cgmSteloBigImage from 'images/cgm-stelo-big.png'
import dexcomG7Kit from 'images/dexcom-g7-kit.webp'
import { CurrentUserQuery, DexcomConnectionKind, UserReferralPartner } from 'types'

export const isDexcomG7Partner = (partner: string | null | undefined) =>
  partner === UserReferralPartner.DexcomG7

const dexcomVideoCallPartners = new Set<string>([
  UserReferralPartner.Dexcom,
  UserReferralPartner.Stelo,
  UserReferralPartner.DexcomG7
])

export const isDexcomVideoCallPartner = (partner: string | null | undefined) =>
  !!partner && dexcomVideoCallPartners.has(partner)

const steloIntegrationConfig = {
  title: 'Connect Your Stelo Account',
  image: cgmUpsell,
  imageAlt: 'Biosensor',
  imageMaxWidth: 310,
  bannerImage: cgmSteloBigImage,
  bannerImageAlt: 'Dexcom Stelo Sensor',
  accountInstructions:
    'If you already have a Stelo account, please connect using your existing Stelo credentials. If you do not yet have a Stelo account, you will be able to create one during the connection step.',
  appointmentPrompt: 'Please connect your Stelo account on the next step',
  clientIdField: 'dexcomSteloClientId',
  connectionField: 'dexcomSteloConnection',
  connectionKind: DexcomConnectionKind.Stelo,
  missingAuthorizationCodeMessage:
    'Authorization code not found, please try to connect your Stelo account again.',
  failureMessage: 'Failed to connect Stelo account, please try again.',
  successMessage: 'Your Stelo account has been successfully connected',
  analytics: {
    connect: VideoCalls.SteloIntegrateAccount,
    connectLater: VideoCalls.SteloIntegrateAccountNotNow,
    bannerClick: VideoCalls.SteloIntegrateAccountBannerClicked,
    success: VideoCalls.SteloIntegrateSuccessfully,
    failure: VideoCalls.SteloIntegrateNotSuccess
  }
} as const

const dexcomG7IntegrationConfig = {
  title: 'Connect Your Dexcom G7 Sensor',
  image: dexcomG7Kit,
  imageAlt: 'Dexcom G7 sensor',
  imageMaxWidth: 280,
  bannerImage: dexcomG7,
  bannerImageAlt: 'Dexcom G7 sensor',
  accountInstructions:
    'If you already have a Dexcom account, please connect using your existing Dexcom credentials. If you do not yet have a Dexcom account, you will be able to create one during the connection step.',
  appointmentPrompt: 'Please connect your Dexcom G7 sensor on the next step',
  clientIdField: 'dexcomClientId',
  connectionField: 'dexcomConnection',
  // Dexcom G7 uses the backend's legacy `research` connection kind. Omitting
  // sensorModel lets the backend classify the connection as G7 rather than G6.
  connectionKind: DexcomConnectionKind.Research,
  missingAuthorizationCodeMessage:
    'Authorization code not found, please try to connect your Dexcom G7 sensor again.',
  failureMessage: 'Failed to connect your Dexcom G7 sensor, please try again.',
  successMessage: 'Your Dexcom G7 sensor has been successfully connected',
  analytics: {
    connect: VideoCalls.DexcomG7IntegrateAccount,
    connectLater: VideoCalls.DexcomG7IntegrateAccountNotNow,
    bannerClick: VideoCalls.DexcomG7IntegrateAccountBannerClicked,
    success: VideoCalls.DexcomG7IntegrateSuccessfully,
    failure: VideoCalls.DexcomG7IntegrateNotSuccess
  }
} as const

type DexcomIntegrationConfig = typeof steloIntegrationConfig | typeof dexcomG7IntegrationConfig

export const getDexcomIntegrationConfig = (
  partner: string | null | undefined
): DexcomIntegrationConfig =>
  isDexcomG7Partner(partner) ? dexcomG7IntegrationConfig : steloIntegrationConfig

type UserDexcomConnections = Pick<
  NonNullable<CurrentUserQuery['currentUser']>,
  'dexcomConnection' | 'dexcomSteloConnection'
>

export const getDexcomConnection = (
  user: UserDexcomConnections | null | undefined,
  partner: string | null | undefined
) => user?.[getDexcomIntegrationConfig(partner).connectionField] ?? null
